<script setup lang="ts">
import type { PermissionRoleInDb } from '@/api';
import { IconShieldCheck } from '@/components/design/icons';
import { computed, ref, watch } from 'vue';
import { PermissionsService } from '@/services/permissionsService';
import GaiaSearchInput from '@/components/design/base/inputs/GaiaSearchInput.vue';
import GaiaPagination from '@/components/design/composite/GaiaPagination.vue';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaModal from '@/components/design/base/GaiaModal.vue';
import GaiaTag from '@/components/design/base/GaiaTag.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaEmptyState from '@/components/design/composite/GaiaEmptyState.vue';
import GaiaListItem from '@/components/design/composite/GaiaListItem.vue';
import { gaiaNeutral } from '@/components/design/themes/tokens';
import { useGaiaToast } from '@/composables/useGaiaToast';

interface Props {
  /** Whether the modal is visible (v-model:show) */
  modelValue: boolean;
  /**
   * Roles the user already has. They stay in the list, marked "Assigned",
   * rather than being filtered out: the list is paged by the server, so hiding
   * rows after the fact left pages short or empty, and a search for a role they
   * already hold looked like the role didn't exist.
   */
  assignedRoleUids?: string[];
  /** When set, the Assign button for this role UID shows loading (e.g. while parent is calling API) */
  assigningRoleUid?: string | null;
}

interface Emits {
  (e: 'update:modelValue', value: boolean): void;
  (e: 'assign', role: PermissionRoleInDb): void;
}

const props = withDefaults(defineProps<Props>(), {
  assignedRoleUids: () => [],
  assigningRoleUid: null,
});

const emit = defineEmits<Emits>();

const message = useGaiaToast();
const searchQuery = ref<string>('');
const currentPage = ref<number>(1);
const pageSize = ref<number>(10);
const totalCount = ref<number>(0);
const roles = ref<PermissionRoleInDb[]>([]);
const loading = ref<boolean>(false);

const showModal = computed({
  get: () => props.modelValue,
  set: (value: boolean) => {
    emit('update:modelValue', value);
  },
});

function isAssigned(role: PermissionRoleInDb): boolean {
  return props.assignedRoleUids.includes(role.uid);
}

watch(
  () => props.modelValue,
  (visible: boolean) => {
    if (visible) {
      searchQuery.value = '';
      currentPage.value = 1;
    }
  }
);

watch(
  [(): boolean => props.modelValue, currentPage, pageSize],
  function (): void {
    if (props.modelValue) void fetchRoles();
  },
  { immediate: false }
);

async function fetchRoles(): Promise<void> {
  loading.value = true;
  try {
    const result = await PermissionsService.searchRoles({
      text: searchQuery.value.trim(),
      page: currentPage.value,
      pageSize: pageSize.value,
      sortKey: 'name',
      sortDirection: 'asc',
    });
    roles.value = result.results;
    totalCount.value = result.result_count;
  } catch (error) {
    message.error(error instanceof Error ? error.message : 'Failed to load roles');
    roles.value = [];
    totalCount.value = 0;
  } finally {
    loading.value = false;
  }
}

// The field owns the debounce and fires this at once when it is cleared.
function applySearch(): void {
  currentPage.value = 1;
  void fetchRoles();
}

function handleAssign(role: PermissionRoleInDb): void {
  emit('assign', role);
}
</script>

<template>
  <gaia-modal
    v-model:show="showModal"
    width="560px"
    title="Assign a role"
    :mask-closable="assigningRoleUid == null"
  >
    <n-flex vertical :size="14" :wrap="false" :style="{ minHeight: '280px' }">
      <gaia-search-input
        v-model:value="searchQuery"
        placeholder="Search roles"
        :debounce="400"
        @search="applySearch"
      />

      <n-spin :show="loading">
        <n-flex v-if="roles.length > 0" vertical :size="8" :wrap="false">
          <gaia-list-item
            v-for="role in roles"
            :key="role.uid"
            size="dense"
            :style="isAssigned(role) ? { opacity: 0.6 } : undefined"
          >
            <template #leading>
              <n-icon :component="IconShieldCheck" :size="16" :color="gaiaNeutral.textMuted" />
            </template>
            <n-flex vertical :size="2" :style="{ minWidth: 0 }">
              <n-flex align="center" :size="8" :wrap="false">
                <gaia-text size="body" weight="semibold" tone="ink">{{ role.name }}</gaia-text>
                <gaia-tag v-if="role.protected" variant="muted" size="small">Built-in</gaia-tag>
              </n-flex>
              <gaia-text v-if="role.description" size="control" tone="subtle">
                {{ role.description }}
              </gaia-text>
            </n-flex>
            <template #trailing>
              <gaia-tag v-if="isAssigned(role)" variant="muted" size="small">Assigned</gaia-tag>
              <gaia-button
                v-else
                size="small"
                :loading="assigningRoleUid === role.uid"
                :disabled="assigningRoleUid != null"
                @click="handleAssign(role)"
              >
                Assign
              </gaia-button>
            </template>
          </gaia-list-item>
        </n-flex>

        <gaia-empty-state
          v-else-if="!loading"
          :title="searchQuery.trim() ? 'No matching roles' : 'No roles yet'"
        >
          {{
            searchQuery.trim()
              ? 'Try another name, or clear the search.'
              : 'Create a role on the Roles tab first.'
          }}
        </gaia-empty-state>
      </n-spin>

      <gaia-pagination
        v-if="totalCount > pageSize && !loading"
        v-model:page="currentPage"
        v-model:page-size="pageSize"
        :total="totalCount"
        :page-sizes="[5, 10, 25, 50]"
      />
    </n-flex>
  </gaia-modal>
</template>
